import React, { useState } from "react";
import SignupModal from "./widget/SignupModal";
import FindPasswordModal from "./widget/FindPasswordModal";
import useAuth from "../../hooks/useAuth";
import { useZoomSettings } from "../../hooks/useZoomSettings";
import "./Login.css";
import { Helmet } from "react-helmet-async";
import { useNavigate, useLocation } from "react-router-dom";
import LoginMobile from "./LoginMobile";
import { fetchKeyword } from "../../components/test.js";
import {
  saveCredentials as saveCredentialsToStorage,
  loadCredentials,
  clearCredentials,
} from "../../utils/encryption";
import webLoginImage from "../../assets/web_login_image.png";

const Login = () => {
  const [username, setId] = useState("");
  const [password, setPassword] = useState("");
  const [autoCheck, setKeepLogin] = useState(false);
  const [saveCredentials, setSaveCredentials] = useState(false);
  const [isSignupModalOpen, setIsSignupModalOpen] = useState(false);
  const [isFindPasswordModalOpen, setIsFindPasswordModalOpen] = useState(false);
  const { login, loading, error: authError, user } = useAuth();
  const { settings } = useZoomSettings();
  const navigate = useNavigate();
  const location = useLocation();

  // 저장된 자격증명 불러오기
  React.useEffect(() => {
    const credentials = loadCredentials();
    if (credentials) {
      setId(credentials.username);
      setPassword(credentials.password);
      setSaveCredentials(true); // 저장된 자격증명이 있으면 체크박스도 체크
    }
  }, []);

  // 테스트용: 수동으로 아이디 저장하기 (개발용)
  const testSaveUsername = () => {
    localStorage.setItem("savedUsername", "testuser");
    // console.log("테스트 아이디 저장됨");
  };

  // 모바일 감지
  const [isMobile, setIsMobile] = useState(false);

  React.useEffect(() => {
    // 초기 모바일 감지 (한 번만, 더 정확한 방법 사용)
    const initialWidth = window.visualViewport?.width || window.innerWidth;
    const initialIsMobile = initialWidth <= 768;
    setIsMobile(initialIsMobile);
    
    // 모바일로 감지되면 리사이즈 이벤트 무시 (옛날 디자인으로 넘어가는 것 방지)
    if (initialIsMobile) {
      return;
    }
    
    // 데스크톱인 경우에만 리사이즈 이벤트 처리
    const checkMobile = () => {
      const width = window.visualViewport?.width || window.innerWidth;
      setIsMobile(width <= 768);
    };

    // 디바운싱하여 너무 자주 발생하지 않도록
    let resizeTimeout;
    const handleResize = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(checkMobile, 100);
    };
    
    window.addEventListener("resize", handleResize);
    if (window.visualViewport) {
      window.visualViewport.addEventListener("resize", handleResize);
    }

    return () => {
      window.removeEventListener("resize", handleResize);
      if (window.visualViewport) {
        window.visualViewport.removeEventListener("resize", handleResize);
      }
      clearTimeout(resizeTimeout);
    };
  }, []);

  // URL 정규화 및 캐시 방지 (최우선 실행)
  React.useEffect(() => {
    const currentPath = window.location.pathname;

    // 1. /login/로 끝나는 경우 /login으로 리다이렉트 (옛날 UI 방지)
    if (currentPath === "/login/") {
      const search = window.location.search;
      const hash = window.location.hash;
      // 즉시 리다이렉트 (캐시 방지를 위해 쿼리 파라미터 추가)
      const timestamp = Date.now();
      window.location.replace(
        `/login?_t=${timestamp}&_nocache=1${
          search ? `&${search.slice(1)}` : ""
        }${hash}`
      );
      return;
    }

    // 2. 로그아웃 후 로그인 페이지로 온 경우 (캐시 방지)
    const params = new URLSearchParams(location.search);
    const noCache = params.get("_nocache");
    const timestamp = params.get("_t");

    if (noCache === "1" && timestamp) {
      // 모든 스크립트와 스타일시트를 다시 로드하도록 강제
      const scripts = document.querySelectorAll("script[src]");
      const links = document.querySelectorAll('link[rel="stylesheet"]');

      scripts.forEach((script) => {
        const src = script.getAttribute("src");
        if (src && !src.includes("_t=")) {
          const separator = src.includes("?") ? "&" : "?";
          script.setAttribute("src", `${src}${separator}_t=${timestamp}`);
        }
      });

      links.forEach((link) => {
        const href = link.getAttribute("href");
        if (href && !href.includes("_t=")) {
          const separator = href.includes("?") ? "&" : "?";
          link.setAttribute("href", `${href}${separator}_t=${timestamp}`);
        }
      });

      // 쿼리 파라미터는 나중에 제거 (페이지 로드 완료 후)
      setTimeout(() => {
        if (window.history.replaceState) {
          window.history.replaceState({}, "", "/login");
        }
      }, 100);
    }
  }, [location.search, location.pathname]);

  // 카카오톡 문의하기 클릭 핸들러
  const handleKakaoInquiry = () => {
    // fetchKeyword 함수 실행
    fetchKeyword("강아지노즈워크장난감,강아지노즈워크,노즈워크장난감");

    // 기존 카카오톡 문의 로직도 유지
    if (settings?.kakaoChannelId) {
      window.open(settings.kakaoChannelId, "_blank");
    } else {
      console.warn("카카오톡 채널 ID가 설정되지 않았습니다.");
      alert("카카오톡 문의 준비 중입니다.");
    }
  };

  // Redirect if already logged in
  React.useEffect(() => {
    const getRedirectInfo = () => {
      const params = new URLSearchParams(location.search);
      const redirect = params.get("redirect");
      const tab = params.get("tab");
      return { redirect, tab };
    };

    if (user) {
      const { redirect, tab } = getRedirectInfo();

      if (redirect === "home" && tab) {
        navigate(`/?tab=${tab}`);
      } else {
        navigate("/");
      }
    }
  }, [user, navigate, location.search]);

  const handleLogin = async (e) => {
    e.preventDefault();

    // 로딩 중이면 중복 요청 방지
    if (loading) {
      return;
    }

    // 아이디와 비밀번호 검증
    if (!username || !username.trim()) {
      alert("아이디를 입력해주세요.");
      return;
    }

    if (!password || !password.trim()) {
      alert("비밀번호를 입력해주세요.");
      return;
    }

    try {
      // 로그인 전 기존 세션과 토큰 모두 삭제
      // sessionStorage 정리
      try {
        sessionStorage.removeItem("token");
        sessionStorage.removeItem("refresh_token");
        sessionStorage.removeItem("user");
        sessionStorage.removeItem("userInfoLoaded");
        sessionStorage.removeItem("zoomSettings");
        sessionStorage.removeItem("zoomNotification");
      } catch (storageError) {
        console.warn("세션 정리 중 오류:", storageError);
      }

      // 메모리 저장소 정리
      if (window._tempStorage) {
        Object.keys(window._tempStorage).forEach((key) => {
          if (
            key.includes("token") ||
            key.includes("user") ||
            key.includes("zoom")
          ) {
            delete window._tempStorage[key];
          }
        });
      }

      const loginData = {
        username,
        password,
        autoCheck,
        auto: true,
        // audit: true,
      };

      const result = await login(loginData);

      // 로그인 성공 시 자격증명 저장 (체크박스가 선택된 경우에만)
      if (result && result.success && saveCredentials) {
        saveCredentialsToStorage(username, password);
      }
    } catch (error) {
      console.error("로그인 처리 중 오류:", error);
      // 에러는 useAuth의 login 함수에서 이미 처리되므로 여기서는 추가 처리 불필요
    }
  };

  // 모바일이면 모바일 컴포넌트 렌더링
  if (isMobile) {
    return <LoginMobile />;
  }

  return (
    <div className="login-root">
      <Helmet>
        <title>
          올려줌 - 인스타그램, 유튜브, 틱톡, 스레드, 스마트스토어,
          네이버플레이스까지 검색·방문·리뷰·저장·노출을 실제 유저가 직접
          실행하는 리워드 트래픽 기반 마케팅 셀프마케팅의 해답, 셀러 대행사 중심
          실행 플랫폼 올려줌.
        </title>
        <meta
          name="description"
          content="인스타그램, 유튜브, 틱톡, 스레드, 스마트스토어, 네이버플레이스까지 검색·방문·리뷰·저장·노출을 실제 유저가 직접 실행하는 리워드 트래픽 기반 마케팅 셀프마케팅의 해답, 셀러 대행사 중심 실행 플랫폼 올려줌."
        />
        <meta name="robots" content="index,follow" />
        <meta
          name="naver-site-verification"
          content="4ef67ad0cd69b0789141bcbef2fde1c58070181c"
        />
        <meta
          name="google-site-verification"
          content="9k6Q7LWodKvZcvbcP5AbOLZEu9xEkVZ_CSMVcQoy2pU"
        />
        <meta property="og:title" content="올려줌" />
        <meta
          property="og:description"
          content="인스타그램, 유튜브, 틱톡, 스레드, 스마트스토어, 네이버플레이스까지 검색·방문·리뷰·저장·노출을 실제 유저가 직접 실행하는 리워드 트래픽 기반 마케팅 셀프마케팅의 해답, 셀러 대행사 중심 실행 플랫폼 올려줌."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.olzoom/login" />
      </Helmet>

      <div className="login-card">
        <header className="login-header">
          <h1 className="login-title">올려줌</h1>
          <p className="login-subtitle">간편하고 쉬운 셀프 마케팅 플랫폼</p>
        </header>

        <form
          className="login-form"
          onSubmit={(e) => {
            e.preventDefault();
            e.stopPropagation();
            handleLogin(e);
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              e.stopPropagation();
            }
          }}
        >
          <div
            className={`login-input-wrapper ${
              username && username.trim() ? "has-value" : ""
            }`}
          >
            <label htmlFor="username" className="sr-only">
              아이디
            </label>
            <input
              id="username"
              type="text"
              value={username}
              onChange={(e) => setId(e.target.value)}
              placeholder="아이디"
              className="login-input"
              autoComplete="username"
              aria-label="아이디"
            />
          </div>

          <div
            className={`login-input-wrapper ${
              password && password.trim() ? "has-value" : ""
            }`}
          >
            <label htmlFor="password" className="sr-only">
              비밀번호
            </label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="비밀번호"
              className="login-input"
              autoComplete="current-password"
              aria-label="비밀번호"
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  e.stopPropagation();
                  handleLogin(e);
                }
              }}
            />
          </div>

          <div className="login-checkbox-wrapper">
            <label htmlFor="saveCredentials" className="login-checkbox-label">
              <div className="login-checkbox-container">
                <input
                  id="saveCredentials"
                  type="checkbox"
                  checked={saveCredentials}
                  onChange={(e) => setSaveCredentials(e.target.checked)}
                  className="login-checkbox-input"
                  aria-label="아이디와 비밀번호 저장"
                />
                <div
                  className={`login-checkbox-custom ${
                    saveCredentials ? "checked" : ""
                  }`}
                />
                {saveCredentials && (
                  <svg
                    className="login-checkbox-checkmark"
                    viewBox="0 0 9 7"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M1 3.5L3.5 6L8 1"
                      stroke="white"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                )}
              </div>
              <span className="login-checkbox-text">
                아이디와 비밀번호 저장
              </span>
            </label>
          </div>

          <div className="login-buttons-wrapper">
            <button
              type="submit"
              className="login-submit-btn"
              disabled={loading}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleLogin(e);
              }}
              aria-label="로그인"
            >
              {loading ? "로그인 중..." : "로그인"}
            </button>

            <div className="login-action-buttons">
              <button
                type="button"
                onClick={() => setIsSignupModalOpen(true)}
                className="login-action-btn"
                aria-label="회원가입"
              >
                회원가입
              </button>
              <button
                type="button"
                onClick={() => setIsFindPasswordModalOpen(true)}
                className="login-action-btn"
                aria-label="아이디/비밀번호 찾기"
              >
                아이디/비밀번호 찾기
              </button>
            </div>
          </div>

          {authError && <div className="login-error-message">{authError}</div>}
        </form>

        <div className="login-footer">
          <p className="login-help-text">
            로그인이 되지 않는 경우 카카오톡으로 문의바랍니다.
          </p>
          <button
            type="button"
            onClick={handleKakaoInquiry}
            className="login-kakao-btn"
            aria-label="카카오톡 문의하기"
          >
            카카오톡 문의하기
          </button>
        </div>
      </div>

      <div className="login-image-wrapper">
        <img
          className="login-image"
          alt="Digital Marketing illustration showing grow your business with good marketing"
          src={webLoginImage}
        />
      </div>

      {/* 회원가입 모달 */}
      <SignupModal
        isOpen={isSignupModalOpen}
        onClose={() => setIsSignupModalOpen(false)}
      />

      {/* 비밀번호 찾기 모달 */}
      <FindPasswordModal
        isOpen={isFindPasswordModalOpen}
        onClose={() => setIsFindPasswordModalOpen(false)}
      />
    </div>
  );
};

export default Login;
